import { useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  BarChart2,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  GitMerge,
  Layers,
  Loader2,
  Plus,
  Scissors,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import type { SplitCurriculumChapter, CurriculumSplitResponse, ConfirmSplitResponse, ContentFigure } from "../../lib/api";
import { Button } from "../ui/Button";
import { GeneratedContent } from "../GeneratedContent";
import { AdminManualPacketModal } from "./AdminManualPacketModal";
import { Modal } from "../ui/Modal";

interface AdminChapterSplitModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceId: string;
  token: string;
  onConfirmed?: () => void;
}

interface EditableChapter {
  id?: string;
  title: string;
  sortOrder: number;
  startPage: number | "";
  endPage: number | "";
  topics: Array<{ id?: string; title: string }>;
  figures?: ContentFigure[];
  figureCount?: number;
  content?: SplitCurriculumChapter["content"];
  exerciseCount?: number;
  exercises?: Array<{ text: string; section?: string }>;
}

export function AdminChapterSplitModal({
  isOpen,
  onClose,
  resourceId,
  token,
  onConfirmed,
}: AdminChapterSplitModalProps) {
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<ConfirmSplitResponse | null>(null);

  const [splitData, setSplitData] = useState<CurriculumSplitResponse | null>(null);
  const [chapters, setChapters] = useState<EditableChapter[]>([]);
  const [expandedPreview, setExpandedPreview] = useState<number | null>(null);
  const [manualPacketChapter, setManualPacketChapter] = useState<{
    id: string;
    title: string;
    topics: Array<{ id?: string; title: string }>;
  } | null>(null);
  const [generatingChapterId, setGeneratingChapterId] = useState<string | null>(null);
  const [genNotice, setGenNotice] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const handleGenerateChapter = async (ch: EditableChapter) => {
    if (!token || !ch.id) return;
    setGeneratingChapterId(ch.id);
    setGenNotice(null);
    try {
      const res = await api.admin.createGenerationJob(token, {
        resourceId,
        chapterId: ch.id,
        kind: "all",
        targetCount: 100,
      });
      setGenNotice({
        type: "success",
        message: `Background AI generation job started for "${ch.title}" (Job ID: ${res.jobId}). You can review generated drafts in the Review Queue.`,
      });
    } catch (err) {
      setGenNotice({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to trigger generation job",
      });
    } finally {
      setGeneratingChapterId(null);
    }
  };

  useEffect(() => {
    if (!isOpen || !resourceId || !token) return;
    setSuccessResult(null);
    setError(null);
    setLoading(true);

    api.admin
      .getCurriculumSplit(token, resourceId)
      .then((data) => {
        setSplitData(data);
        const mapped: EditableChapter[] = data.chapters.map((ch, idx) => ({
          id: ch.id,
          title: ch.title,
          sortOrder: ch.sortOrder ?? idx,
          startPage: ch.startPage ?? "",
          endPage: ch.endPage ?? "",
          topics: ch.topics || [],
          figures: ch.figures || [],
          figureCount: ch.figureCount ?? (ch.figures?.length || 0),
          content: ch.content,
          exerciseCount: ch.exerciseCount ?? 0,
          exercises: ch.exercises ?? [],
        }));
        setChapters(mapped);
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : "Failed to load chapter split");
      })
      .finally(() => setLoading(false));
  }, [isOpen, resourceId, token]);

  if (!isOpen) return null;

  const expectedCount = splitData?.expectedChapterCount;
  const hasMismatch = Boolean(expectedCount && chapters.length !== expectedCount);

  // Chapter editing operations
  const updateChapterTitle = (idx: number, newTitle: string) => {
    setChapters((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, title: newTitle } : c)),
    );
  };

  const updateStartPage = (idx: number, val: string) => {
    const num = val === "" ? "" : Math.max(1, parseInt(val, 10) || 1);
    setChapters((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, startPage: num } : c)),
    );
  };

  const updateEndPage = (idx: number, val: string) => {
    const num = val === "" ? "" : Math.max(1, parseInt(val, 10) || 1);
    setChapters((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, endPage: num } : c)),
    );
  };

  const mergeWithNext = (idx: number) => {
    if (idx >= chapters.length - 1) return;
    const cur = chapters[idx];
    const nxt = chapters[idx + 1];

    const merged: EditableChapter = {
      title: `${cur.title} & ${nxt.title}`,
      sortOrder: cur.sortOrder,
      startPage: cur.startPage !== "" ? cur.startPage : nxt.startPage,
      endPage: nxt.endPage !== "" ? nxt.endPage : cur.endPage,
      topics: [...cur.topics, ...nxt.topics],
      exerciseCount: (cur.exerciseCount || 0) + (nxt.exerciseCount || 0),
    };

    setChapters((prev) => {
      const next = [...prev];
      next.splice(idx, 2, merged);
      return next.map((c, i) => ({ ...c, sortOrder: i }));
    });
  };

  const splitChapter = (idx: number) => {
    const cur = chapters[idx];
    const start = typeof cur.startPage === "number" ? cur.startPage : 1;
    const end = typeof cur.endPage === "number" ? cur.endPage : start + 10;
    const mid = Math.floor((start + end) / 2);

    const part1: EditableChapter = {
      title: `${cur.title} (Part 1)`,
      sortOrder: idx,
      startPage: start,
      endPage: mid,
      topics: cur.topics.slice(0, Math.ceil(cur.topics.length / 2)),
    };

    const part2: EditableChapter = {
      title: `${cur.title} (Part 2)`,
      sortOrder: idx + 1,
      startPage: mid + 1,
      endPage: end,
      topics: cur.topics.slice(Math.ceil(cur.topics.length / 2)),
    };

    setChapters((prev) => {
      const next = [...prev];
      next.splice(idx, 1, part1, part2);
      return next.map((c, i) => ({ ...c, sortOrder: i }));
    });
  };

  const deleteChapter = (idx: number) => {
    if (chapters.length <= 1) return;
    setChapters((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((c, i) => ({ ...c, sortOrder: i }));
    });
  };

  const addChapter = () => {
    const lastChapter = chapters[chapters.length - 1];
    const lastEnd = lastChapter && typeof lastChapter.endPage === "number" ? lastChapter.endPage : 0;
    const newCh: EditableChapter = {
      title: `Unit ${chapters.length + 1}: Chapter Title`,
      sortOrder: chapters.length,
      startPage: lastEnd > 0 ? lastEnd + 1 : "",
      endPage: lastEnd > 0 ? lastEnd + 20 : "",
      topics: [],
    };
    setChapters((prev) => [...prev, newCh]);
  };

  const handleConfirm = async () => {
    if (chapters.length === 0) return;
    setError(null);
    setConfirming(true);

    try {
      const payload = chapters.map((c, idx) => ({
        id: c.id,
        title: c.title.trim(),
        sort_order: idx,
        start_page: typeof c.startPage === "number" ? c.startPage : undefined,
        end_page: typeof c.endPage === "number" ? c.endPage : undefined,
      }));

      const res = await api.admin.confirmCurriculumSplit(token, resourceId, payload);
      setSuccessResult(res);
      if (onConfirmed) onConfirmed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to confirm chapter split");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        showCloseButton={false}
        className="p-0 overflow-hidden max-w-4xl max-h-[90vh]"
      >
        <div className="w-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/40">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xs bg-primary/10 text-primary flex items-center justify-center">
              <BookOpen size={18} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                Chapter Split Review & Cleaning
                {splitData?.status && (
                  <span
                    className={`text-xs px-2.5 py-0.5 rounded-xs font-semibold uppercase tracking-wider ${
                      splitData.status === "confirmed"
                        ? "bg-success/10 text-success"
                        : "bg-info/10 text-info"
                    }`}
                  >
                    {splitData.status}
                  </span>
                )}
              </h2>
              <p className="text-xs text-muted-foreground truncate max-w-xl">
                {splitData?.resourceTitle || "Review and edit module curriculum split"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading && (
            <div className="py-16 flex flex-col items-center justify-center text-muted-foreground gap-3">
              <Loader2 size={32} className="animate-spin text-primary" />
              <p className="text-sm font-medium">Extracting and loading chapter structure…</p>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive flex items-start gap-3">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <div className="text-sm">{error}</div>
            </div>
          )}

          {successResult && (
            <div className="p-4 rounded-xl bg-success/10 border border-success/30 text-success space-y-2">
              <div className="flex items-center gap-2 font-bold text-sm">
                <CheckCircle2 size={18} className="text-success" />
                Split Confirmed & Clean Content Stored Successfully!
              </div>
              <p className="text-xs text-success leading-relaxed">
                Stored cleaned teaching text for <strong>{successResult.chapterCount} chapters</strong>. All front matter
                (cover, table of contents, lists of figures/tables, preface, running headers/footers) has been removed.
                {successResult.totalExercises > 0 && (
                  <span>
                    {" "}
                    <strong>{successResult.totalExercises} exercises</strong> excluded and preserved for question generation.
                  </span>
                )}
              </p>
            </div>
          )}

          {genNotice && (
            <div
              className={`p-4 rounded-xl border flex items-start justify-between gap-3 ${
                genNotice.type === "success"
                  ? "bg-success/10 border-success/30 text-success"
                  : "bg-destructive/10 border-destructive/20 text-destructive"
              }`}
            >
              <div className="flex items-start gap-2.5 text-xs font-medium">
                {genNotice.type === "success" ? (
                  <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-success" />
                ) : (
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                )}
                <span>{genNotice.message}</span>
              </div>
              <button
                type="button"
                onClick={() => setGenNotice(null)}
                className="text-xs opacity-70 hover:opacity-100"
              >
                ✕
              </button>
            </div>
          )}

          {!loading && (
            <>
              {/* Mismatch Alert Banner */}
              {hasMismatch ? (
                <div className="p-4 rounded-xl bg-warning/10 border border-warning/30 text-warning flex items-start gap-3">
                  <AlertTriangle size={20} className="text-warning mt-0.5 flex-shrink-0" />
                  <div className="space-y-1 text-sm">
                    <p className="font-bold">
                      Chapter Count Mismatch Detected
                    </p>
                    <p className="text-xs text-warning">
                      The module was uploaded with an expected count of <strong>{expectedCount} chapters</strong>, but the
                      pipeline proposed <strong>{chapters.length} chapters</strong>. Use the buttons below to merge, split,
                      or rename chapters so the structure matches the textbook exactly.
                    </p>
                  </div>
                </div>
              ) : expectedCount ? (
                <div className="p-3 rounded-xl bg-info/10 border border-info/30 text-info flex items-center gap-2 text-xs">
                  <CheckCircle2 size={16} className="text-info" />
                  <span>
                    Proposed chapter count (<strong>{chapters.length}</strong>) matches expected count (
                    <strong>{expectedCount}</strong>).
                  </span>
                </div>
              ) : null}

              {/* Instructions Bar */}
              <div className="bg-muted/50 p-3 rounded-xl border border-border text-xs text-muted-foreground flex items-center justify-between">
                <span>
                  Unit/Chapter titles are preserved as written. Only instructional content (definitions, formulas, examples)
                  will be stored. Front matter and exercises are automatically stripped.
                </span>
                <button
                  type="button"
                  onClick={addChapter}
                  className="px-3 py-1.5 rounded-lg bg-background border border-border text-foreground hover:bg-muted font-medium flex items-center gap-1.5 shadow-sm transition-all"
                >
                  <Plus size={14} /> Add Chapter
                </button>
              </div>

              {/* Chapter Cards List */}
              <div className="space-y-4">
                {chapters.map((ch, idx) => (
                  <div
                    key={ch.id || `ch-${idx}`}
                    className="p-4 rounded-xl border border-border bg-card shadow-sm hover:border-primary/40 transition-colors space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      {/* Chapter Title & Number */}
                      <div className="flex items-center gap-3 flex-1">
                        <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary font-bold text-xs flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <input
                          type="text"
                          value={ch.title}
                          onChange={(e) => updateChapterTitle(idx, e.target.value)}
                          placeholder="e.g. Unit 3: Vectors and Matrices"
                          className="flex-1 font-semibold text-sm px-3 py-1.5 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                      </div>

                      {/* Page Range Inputs */}
                      <div className="flex items-center gap-2 self-start sm:self-auto text-xs">
                        <span className="text-muted-foreground">Pages:</span>
                        <input
                          type="number"
                          min={1}
                          value={ch.startPage}
                          onChange={(e) => updateStartPage(idx, e.target.value)}
                          placeholder="Start"
                          className="w-16 px-2 py-1.5 rounded-lg border border-border text-center font-mono text-xs bg-background"
                        />
                        <span className="text-muted-foreground">—</span>
                        <input
                          type="number"
                          min={1}
                          value={ch.endPage}
                          onChange={(e) => updateEndPage(idx, e.target.value)}
                          placeholder="End"
                          className="w-16 px-2 py-1.5 rounded-lg border border-border text-center font-mono text-xs bg-background"
                        />
                      </div>
                    </div>

                    {/* Detected Topics Pills */}
                    {ch.topics && ch.topics.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        <span className="text-xs text-muted-foreground font-medium mr-1">Topics:</span>
                        {ch.topics.slice(0, 6).map((t, tIdx) => (
                          <span
                            key={t.id || tIdx}
                            className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground border border-border/60 truncate max-w-[200px]"
                            title={t.title}
                          >
                            {t.title}
                          </span>
                        ))}
                        {ch.topics.length > 6 && (
                          <span className="text-xs text-muted-foreground font-semibold">
                            +{ch.topics.length - 6} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Figure & Exercise Badges */}
                    {((ch.figureCount !== undefined && ch.figureCount > 0) || (ch.exerciseCount !== undefined && ch.exerciseCount > 0)) && (
                      <div className="flex flex-wrap items-center gap-2 pt-0.5">
                        {ch.figureCount !== undefined && ch.figureCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md bg-info/10 text-info border border-info/30">
                            <BarChart2 size={12} className="text-info" />
                            {ch.figureCount} figure{ch.figureCount === 1 ? "" : "s"} detected
                          </span>
                        )}
                        {ch.exerciseCount !== undefined && ch.exerciseCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground border border-border">
                            <FileText size={12} className="text-primary" />
                            {ch.exerciseCount} exercises extracted
                          </span>
                        )}
                      </div>
                    )}

                    {/* Stored Content Preview Section */}
                    {ch.content && (
                      <div className="pt-2 border-t border-border/60">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-success font-semibold flex items-center gap-1">
                            <CheckCircle2 size={13} /> Clean Content Stored (v{ch.content.version} · {ch.content.wordCount} words)
                          </span>
                          <button
                            type="button"
                            onClick={() => setExpandedPreview(expandedPreview === idx ? null : idx)}
                            className="text-primary hover:underline flex items-center gap-1 font-medium"
                          >
                            {expandedPreview === idx ? (
                              <>
                                Hide Preview <ChevronUp size={13} />
                              </>
                            ) : (
                              <>
                                View Clean Text <ChevronDown size={13} />
                              </>
                            )}
                          </button>
                        </div>

                        {expandedPreview === idx && (
                          <div className="mt-2 p-3 bg-muted/60 rounded-lg text-xs font-mono text-foreground whitespace-pre-wrap max-h-48 overflow-y-auto admin-scroll-y border border-border/80">
                            {ch.content.preview || "No content preview available."}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Chapter Operation Actions */}
                    <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                      {ch.id && (
                        <>
                          <button
                            type="button"
                            disabled={generatingChapterId === ch.id}
                            onClick={() => handleGenerateChapter(ch)}
                            className="px-2.5 py-1 rounded-md text-xs font-semibold text-success hover:bg-success/10 hover:bg-success/20 flex items-center gap-1.5 transition-colors border border-success/30 bg-success/10 disabled:opacity-50"
                            title="Trigger background AI question bank and notes generation"
                          >
                            <Sparkles size={13} /> {generatingChapterId === ch.id ? "Starting…" : "Generate AI Bank"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setManualPacketChapter({ id: ch.id!, title: ch.title, topics: ch.topics || [] })}
                            className="px-2.5 py-1 rounded-md text-xs font-semibold text-primary hover:bg-primary/10 flex items-center gap-1.5 transition-colors border border-primary/30 bg-primary/5"
                            title="Export Manual Generation Packet for this chapter"
                          >
                            <Layers size={13} /> Export Manual Packet
                          </button>
                        </>
                      )}
                      {idx < chapters.length - 1 && (
                        <button
                          type="button"
                          onClick={() => mergeWithNext(idx)}
                          className="px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 transition-colors"
                          title="Merge with next chapter"
                        >
                          <GitMerge size={12} /> Merge with next
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => splitChapter(idx)}
                        className="px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 transition-colors"
                        title="Split this chapter in half"
                      >
                        <Scissors size={12} /> Split
                      </button>
                      {chapters.length > 1 && (
                        <button
                          type="button"
                          onClick={() => deleteChapter(idx)}
                          className="px-2 py-1 rounded-md text-xs font-medium text-destructive hover:bg-destructive/10 flex items-center gap-1 transition-colors"
                          title="Delete chapter"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            <strong>{chapters.length}</strong> chapter(s) ready to clean and store.
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={confirming || chapters.length === 0}
              loading={confirming}
              loadingText="Cleaning & Storing Content…"
              variant="primary"
              size="md"
              className="px-5 py-2"
            >
              Confirm Split & Clean Content
            </Button>
          </div>
        </div>
        </div>
      </Modal>

      {/* Manual Export Packet Sub-modal */}
      {manualPacketChapter && (
        <AdminManualPacketModal
          isOpen={true}
          onClose={() => setManualPacketChapter(null)}
          resourceId={resourceId}
          chapterId={manualPacketChapter.id}
          chapterTitle={manualPacketChapter.title}
          topics={manualPacketChapter.topics}
          token={token}
        />
      )}
    </>
  );
}
